import React, { useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RuleIcon from '@mui/icons-material/Rule';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { hasDenialsAccess } from '../../../../profile';
import {
  DENIALS_NEEDS_INFO_PATH,
  DENIALS_QUEUE_PATH,
  DENIALS_RULES_PATH,
} from '../../../../denials';

const MENU_STYLES = {
    navList: { border: 'black solid 1px' },
    nestedListItem: { marginTop: '0px', paddingLeft: '48px' },
    icon: { color: 'white' },
} as const;

/**
 * "Denials" nav group (FR-013, US1) — gated by the **Denials** claim, on ClaimsMenu's shape: one
 * collapsible group whose top entry only opens and closes it. The group now carries three entries,
 * the worklist, Rules and Needs Info, all for the holder of the Denials claim.
 *
 * The group carries no center-scoped entry, unlike Revenue Recognition's: a denials queue opens
 * across every facility and is narrowed by payer inside the page, so there is nothing to scope.
 */
function DenialsMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: profile } = useGetMyProfileQuery();

  if (!hasDenialsAccess(profile)) {
    return null;
  }

  return (
    <AuthenticatedTemplate>
      <List component="nav" sx={MENU_STYLES.navList}>
        <ListItemButton onClick={() => setOpen(!open)} data-testid="denials-menu-group">
          <ListItemText primary="Denials" />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <ListItemButton
            sx={MENU_STYLES.nestedListItem}
            onClick={() => navigate(DENIALS_QUEUE_PATH)}
            data-testid="denials-menu-worklist"
          >
            <ListItemIcon><ChecklistIcon sx={MENU_STYLES.icon} /></ListItemIcon>
            <ListItemText primary="Worklist" />
          </ListItemButton>
          <ListItemButton
            sx={MENU_STYLES.nestedListItem}
            onClick={() => navigate(DENIALS_RULES_PATH)}
            data-testid="denials-menu-rules"
          >
            <ListItemIcon><RuleIcon sx={MENU_STYLES.icon} /></ListItemIcon>
            <ListItemText primary="Rules" />
          </ListItemButton>
          <ListItemButton
            sx={MENU_STYLES.nestedListItem}
            onClick={() => navigate(DENIALS_NEEDS_INFO_PATH)}
            data-testid="denials-menu-needs-info"
          >
            <ListItemIcon><HelpOutlineIcon sx={MENU_STYLES.icon} /></ListItemIcon>
            <ListItemText primary="Needs Info" />
          </ListItemButton>
        </Collapse>
      </List>
    </AuthenticatedTemplate>
  );
}

export { DenialsMenu };
